import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { SidebarIconComponent } from './sidebar-icon/sidebar-icon.component';

/** Public URL only: scanning never exposes the current user's session. */
export function messengerAppUrl(baseUrl = environment.backofficeMobileUrl): string {
  return new URL(baseUrl).href;
}

@Component({
  selector: 'app-messenger-qr-button', standalone: true,
  imports: [CommonModule, OverlayModule, CdkTrapFocus, MatTooltipModule, TranslateModule, SidebarIconComponent],
  template: `
    <button #trigger="cdkOverlayOrigin" cdkOverlayOrigin type="button" class="messenger-shortcut"
      [class.labelled]="labelled" (click)="toggle()" data-testid="messenger-qr-shortcut"
      aria-haspopup="dialog" [attr.aria-expanded]="open" [attr.aria-label]="'MENU.MESSENGER_QR.TITLE' | translate"
      [matTooltip]="'MENU.MESSENGER_QR.TITLE' | translate" [matTooltipDisabled]="labelled || open" [matTooltipPosition]="tooltipPosition">
      <span class="icon-box"><app-sidebar-icon name="smartphone" [size]="19" [strokeWidth]="1.6"></app-sidebar-icon></span>
      <span *ngIf="labelled">Dashy Messenger</span>
    </button>
    <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="trigger" [cdkConnectedOverlayOpen]="open"
      [cdkConnectedOverlayPositions]="positions" [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop" [cdkConnectedOverlayPush]="true" [cdkConnectedOverlayFlexibleDimensions]="false"
      [cdkConnectedOverlayViewportMargin]="12" (backdropClick)="close()" (detach)="close()" (overlayKeydown)="onOverlayKeydown($event)">
      <section class="messenger-popover" role="dialog" aria-label="Dashy Messenger" data-testid="messenger-qr-popover"
        cdkTrapFocus [cdkTrapFocusAutoCapture]="true" [dir]="tooltipPosition === 'left' ? 'rtl' : 'ltr'">
        <header><span class="app-mark"><app-sidebar-icon name="smartphone" [size]="19" [strokeWidth]="1.6"></app-sidebar-icon></span>
          <strong>Dashy Messenger</strong>
          <button type="button" class="close-button" (click)="close()" [attr.aria-label]="'MENU.MESSENGER_QR.CLOSE' | translate">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>
        <p class="intro">{{ 'MENU.MESSENGER_QR.DESCRIPTION' | translate }}</p>
        <div class="qr-frame"><div #qrHost class="qr-code" role="img" [attr.aria-label]="'MENU.MESSENGER_QR.QR_LABEL' | translate"></div></div>
        <p class="hint">{{ 'MENU.MESSENGER_QR.SCAN_HINT' | translate }}</p>
        <a class="open-app" [href]="appUrl" target="_blank" rel="noopener noreferrer">
          {{ 'MENU.MESSENGER_QR.OPEN' | translate }}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M15 3h6v6M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>
        </a>
        <span class="app-address" dir="ltr">{{ appHost }}</span>
      </section>
    </ng-template>`,
  styles: [`
    :host { display: block; width: 100%; }
    .messenger-shortcut { display: flex; align-items: center; justify-content: center; width: 100%; padding: 6px 0; border: 0; border-radius: 12px; background: transparent; color: #647b88; cursor: pointer; transition: color 160ms ease; }
    .icon-box { display: grid; place-items: center; width: 38px; height: 38px; border-radius: 12px; transition: background 160ms ease; }
    .messenger-shortcut:hover, .messenger-shortcut:focus-visible { color: var(--color-brand-2, #00908e); }
    .messenger-shortcut:hover .icon-box, .messenger-shortcut[aria-expanded=true] .icon-box { background: #e3f3f1; }
    .messenger-shortcut:focus-visible { outline: 2px solid var(--color-brand-2, #00908e); outline-offset: -3px; }
    .labelled { justify-content: flex-start; gap: 6px; font-size: 13px; font-weight: 500; padding: 3px 8px; }
    .messenger-popover { box-sizing: border-box; flex: none; width: 280px; max-width: calc(100vw - 24px); padding: 16px; border: 1px solid rgba(0,144,142,.1); border-radius: 18px; background: #f3fafa; color: var(--color-text-28, #171717); box-shadow: 0 16px 40px rgba(15,23,42,.14); font-family: var(--font-brand-1, Roboto, Arial, sans-serif); }
    header { display: flex; align-items: center; gap: 9px; }
    header strong { flex: 1; font-size: 14px; font-weight: 650; }
    .app-mark { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px; color: var(--color-brand-2, #00908e); background: color-mix(in srgb, var(--color-brand-2, #00908e) 9%, white); }
    .close-button { display: grid; place-items: center; width: 25px; height: 25px; border: 0; border-radius: 50%; color: #82919a; background: transparent; cursor: pointer; }
    .close-button:hover { background: #e6f2f0; color: #172b36; }
    .intro { margin: 11px 0 14px; color: #71818b; font-size: 12px; line-height: 18px; }
    .qr-frame { width: fit-content; margin: auto; padding: 7px; border: 1px solid #e2edeb; border-radius: 15px; background: white; }
    .qr-code { width: 176px; height: 176px; overflow: hidden; background: white; border-radius: 9px; }
    .hint { margin: 12px 4px; color: #71818b; font-size: 11px; line-height: 16px; text-align: center; }
    .open-app { display: flex; align-items: center; justify-content: center; gap: 7px; padding: 10px; border-radius: 10px; background: var(--color-brand-2, #00908e); color: white; text-decoration: none; font-size: 12px; font-weight: 600; }
    .open-app:hover { filter: brightness(.94); }
    .app-address { display: block; margin-top: 8px; text-align: center; font-size: 10px; color: #95a1a8; }
    button:focus-visible, a:focus-visible { outline: 2px solid var(--color-brand-2, #00908e); outline-offset: 2px; }
  `],
})
export class MessengerQrButtonComponent {
  @Input() labelled = false;
  @Input() tooltipPosition: 'left' | 'right' = 'right';
  private qrTarget?: HTMLDivElement;
  @ViewChild('qrHost') set qrHost(ref: ElementRef<HTMLDivElement> | undefined) {
    this.qrTarget = ref?.nativeElement;
    if (!this.qrTarget) return;
    const target = this.qrTarget;
    // The sidebar stays light: download the QR renderer only when the flyout opens.
    void Promise.all([import('qr-code-styling'), import('../../shared/utils/branded-qr.util')]).then(([qr, options]) => {
      if (this.qrTarget !== target) return;
      const logo = new URL('assets/iconLogo.png', document.baseURI).href;
      new qr.default(options.brandedQrOptions(this.appUrl, 176, 'svg', logo)).append(target);
    }).catch(() => { /* The direct app link remains available if the QR chunk is offline. */ });
  }
  readonly appUrl = messengerAppUrl();
  readonly appHost = new URL(this.appUrl).host;
  open = false;
  get positions(): ConnectedPosition[] {
    const right: ConnectedPosition = { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12 };
    const left: ConnectedPosition = { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -12 };
    return this.tooltipPosition === 'left' ? [left, right] : [right, left];
  }
  toggle(): void { this.open = !this.open; }
  close(): void { this.open = false; }
  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
  }
  @HostListener('window:resize') onResize(): void { this.close(); }
}
